import { DashboardViewToggle } from "@/components/dashboard/dashboard-view-toggle";
import { MapErrorBoundary } from "@/components/dashboard/map-error-boundary";
import { AssignToCombobox } from "@/components/inspection/assign-to-combobox";
import {
  type AssignToValue,
  assignToDisplayLabel,
} from "@/components/inspection/assign-to-utils";
import { InspectionDispatchDetailsSheet } from "@/components/inspection/dispatch-details-sheet";
import { InspectionDispatchMap } from "@/components/inspection/dispatch-map";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ErrorComponent from "@/components/ui/error";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/ui/loading-screen";
import { INSPECTION_PORTAL_IMPERSONATE_URL_PARAM } from "@/constants";
import {
  type InspectionPortalLicenseItem,
  bulkAssignInspections,
  ensureInspectionLicenseAccess,
  formatInspectionAddress,
  formatInspectionAssignees,
  inviteInspectionUser,
  listInspectionDispatchLicenses,
  listInspectionOrgUsers,
  unassignInspection,
} from "@/lib/inspection-portal-api";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClipboardCheck, UserRoundX } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

/** Full-page handoff into the inspection SPA (separate Auth0 client/audience). */
export function buildInspectionPortalLicenseUrl(licenseId: string): string {
  const params = new URLSearchParams();
  const impersonateAccount = sessionStorage.get<string>(
    SESSION_KEYS.REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  if (impersonateAccount.trim()) {
    // Operator viewing registrant portal → continue as that org in inspection.
    params.set(
      INSPECTION_PORTAL_IMPERSONATE_URL_PARAM,
      impersonateAccount.trim(),
    );
  }
  const qs = params.toString();
  // Deep-link to perform so "Perform Inspection" opens the workflow directly.
  return `/inspection/${licenseId}/perform${qs ? `?${qs}` : ""}`;
}

export function RegistrantInspectionDispatchPage({
  embedded = false,
  showPerformActions = true,
}: {
  embedded?: boolean;
  showPerformActions?: boolean;
}) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [assignee, setAssignee] = useState<AssignToValue | null>(null);
  const [isMapView, setIsMapView] = useState(false);
  const [performingLicenseId, setPerformingLicenseId] = useState<string | null>(
    null,
  );
  const [unassigningInspectionId, setUnassigningInspectionId] = useState<
    string | null
  >(null);
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(
    null,
  );

  const getToken = () => getAccessTokenSilently();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["inspection-dispatch-licenses"],
    queryFn: () => listInspectionDispatchLicenses(getToken),
    enabled: isAuthenticated,
  });

  const licenses = data ?? [];
  const selectedInspectionItem =
    licenses.find((license) => license.license_id === selectedLicenseId) ??
    null;
  const selectedRegistrantAccountId = useMemo(() => {
    const selectedAccounts = licenses
      .filter((license) => selectedIds.has(license.license_id))
      .map((license) => license.registrant_account_id)
      .filter((id): id is string => Boolean(id));
    return selectedAccounts.length > 0 &&
      selectedAccounts.every((id) => id === selectedAccounts[0])
      ? selectedAccounts[0]
      : undefined;
  }, [licenses, selectedIds]);

  const { data: orgUsers = [] } = useQuery({
    queryKey: ["inspection-org-users", selectedRegistrantAccountId],
    queryFn: () =>
      listInspectionOrgUsers(getToken, selectedRegistrantAccountId),
    enabled: isAuthenticated,
  });

  const allSelected = useMemo(
    () =>
      licenses.length > 0 &&
      licenses.every((l) => selectedIds.has(l.license_id)),
    [licenses, selectedIds],
  );
  const someSelected = useMemo(
    () => licenses.some((l) => selectedIds.has(l.license_id)),
    [licenses, selectedIds],
  );
  const mappableCount = useMemo(
    () =>
      licenses.filter((l) => l.latitude != null && l.longitude != null).length,
    [licenses],
  );
  const showMapToggle = licenses.length > 0 && mappableCount > 0;

  const resetAfterSuccess = () => {
    const ids = Array.from(selectedIds);
    setSelectedIds(new Set());
    setAssignee(null);
    queryClient.invalidateQueries({
      queryKey: ["inspection-dispatch-licenses"],
    });
    queryClient.invalidateQueries({ queryKey: ["inspection-org-users"] });
    void refreshLicensesAfterLocalChange(queryClient, ids);
  };

  const assignMutation = useMutation({
    mutationFn: () => {
      if (assignee?.type !== "user") {
        throw new Error("Select a user to assign");
      }
      return bulkAssignInspections(getToken, {
        license_ids: Array.from(selectedIds),
        assignee_id: assignee.id,
      });
    },
    onSuccess: (result) => {
      toast.success(
        `Assigned ${result.updated_inspection_ids.length} inspection(s)`,
      );
      resetAfterSuccess();
    },
    onError: (err) => {
      toast.error("Assign failed", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => {
      if (assignee?.type !== "invite") {
        throw new Error("Select an email to invite");
      }
      if (selectedIds.size === 0) {
        throw new Error("Select at least one property to assign");
      }
      return inviteInspectionUser(getToken, {
        email: assignee.email,
        name: assignee.name,
        license_ids: Array.from(selectedIds),
      });
    },
    onSuccess: (result) => {
      toast.success(`Invited and assigned ${result.email}`);
      resetAfterSuccess();
    },
    onError: (err) => {
      toast.error("Invite failed", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const performMutation = useMutation({
    mutationFn: async (licenseId: string) => {
      setPerformingLicenseId(licenseId);
      await ensureInspectionLicenseAccess(getToken, {
        license_ids: [licenseId],
      });
      return licenseId;
    },
    onSuccess: (licenseId) => {
      // Hard navigation so the inspection Auth0Provider can SSO with the
      // registrant connection and honor returnTo=/inspection/$licenseId/perform.
      window.location.assign(buildInspectionPortalLicenseUrl(licenseId));
    },
    onError: (err) => {
      toast.error("Unable to open inspection", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
    onSettled: () => {
      setPerformingLicenseId(null);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: async (inspectionId: string) => {
      setUnassigningInspectionId(inspectionId);
      return unassignInspection(getToken, inspectionId);
    },
    onSuccess: () => {
      toast.success("Assignee cleared");
      queryClient.invalidateQueries({
        queryKey: ["inspection-dispatch-licenses"],
      });
      queryClient.invalidateQueries({ queryKey: ["inspection-org-users"] });
      void refreshLicensesAfterLocalChange(queryClient, []);
    },
    onError: (err) => {
      toast.error("Unable to clear assignee", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
    onSettled: () => {
      setUnassigningInspectionId(null);
    },
  });

  const isPending =
    assignMutation.isPending ||
    inviteMutation.isPending ||
    unassignMutation.isPending;
  const canSubmit = selectedIds.size > 0 && assignee != null && !isPending;

  const handleSubmit = () => {
    if (assignee?.type === "invite") {
      inviteMutation.mutate();
      return;
    }
    if (assignee?.type === "user") {
      assignMutation.mutate();
    }
  };

  const toggle = (licenseId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(licenseId)) next.delete(licenseId);
      else next.add(licenseId);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(licenses.map((l) => l.license_id)));
  };

  if (isLoading) {
    return (
      <LoadingScreen message="Loading properties requiring inspection..." />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title="Unable to load dispatch list"
        message={error instanceof Error ? error.message : "Unknown error"}
        action={{ label: "Retry", onClick: () => refetch() }}
      />
    );
  }

  const submitLabel = (() => {
    if (isPending) {
      return assignee?.type === "invite" ? "Inviting…" : "Assigning…";
    }
    if (assignee?.type === "invite") return "Invite & assign";
    return "Assign";
  })();

  const headerCheckboxState = allSelected
    ? true
    : someSelected
      ? ("indeterminate" as const)
      : false;

  return (
    <div
      className={
        embedded
          ? "flex w-full flex-col gap-4"
          : "flex w-full max-w-3xl flex-col gap-4"
      }
    >
      {!embedded ? (
        <Link
          to="/registrant/dashboard"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to dashboard
        </Link>
      ) : null}

      <p className="text-sm text-muted-foreground">
        Assign pending inspections, invite an inspector, or open Perform
        Inspection from a registration row.
      </p>

      {showMapToggle ? (
        <div className="flex justify-end">
          <DashboardViewToggle
            isMapView={isMapView}
            onViewChange={setIsMapView}
          />
        </div>
      ) : null}

      {licenses.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No properties currently require inspection assignment.
        </p>
      ) : (
        <>
          {isMapView && showMapToggle ? (
            <>
              <p className="text-sm text-muted-foreground">
                {selectedIds.size} selected
              </p>
              <MapErrorBoundary
                className="min-h-[320px]"
                onSwitchToListView={() => setIsMapView(false)}
              >
                <InspectionDispatchMap
                  licenses={licenses}
                  selectedIds={selectedIds}
                  onSelectedIdsChange={setSelectedIds}
                />
              </MapErrorBoundary>
            </>
          ) : (
            <DispatchLicensesTable
              pinHeader={!embedded}
              licenses={licenses}
              selectedIds={selectedIds}
              headerCheckboxState={headerCheckboxState}
              onToggleAll={toggleAll}
              onToggle={toggle}
              showPerformActions={showPerformActions}
              performingLicenseId={performingLicenseId}
              onPerform={(licenseId) => performMutation.mutate(licenseId)}
              unassigningInspectionId={unassigningInspectionId}
              onClearAssignee={(inspectionId) => {
                if (
                  confirm(
                    "Clear the assignee from this inspection? Their selected inspection access may also be removed.",
                  )
                ) {
                  unassignMutation.mutate(inspectionId);
                }
              }}
              onOpenDetails={(licenseId) => setSelectedLicenseId(licenseId)}
            />
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="assignee">Assign to</Label>
              <AssignToCombobox
                id="assignee"
                users={orgUsers}
                value={assignee}
                onValueChange={setAssignee}
                disabled={isPending}
              />
              {assignee?.type === "invite" ? (
                <p className="text-xs text-muted-foreground">
                  Will invite {assignToDisplayLabel(assignee)} and assign
                  selected properties.
                </p>
              ) : null}
            </div>
            <Button disabled={!canSubmit} onClick={handleSubmit}>
              {submitLabel}
            </Button>
          </div>
        </>
      )}
      <InspectionDispatchDetailsSheet
        item={selectedInspectionItem}
        open={selectedInspectionItem != null}
        onOpenChange={(open) => {
          if (!open) setSelectedLicenseId(null);
        }}
      />
    </div>
  );
}

function DispatchLicensesTable({
  pinHeader,
  licenses,
  selectedIds,
  headerCheckboxState,
  onToggleAll,
  onToggle,
  showPerformActions,
  performingLicenseId,
  onPerform,
  unassigningInspectionId,
  onClearAssignee,
  onOpenDetails,
}: {
  /** Vertical sticky headers. Off when this table sits inside another list scroller. */
  pinHeader: boolean;
  licenses: InspectionPortalLicenseItem[];
  selectedIds: Set<string>;
  headerCheckboxState: boolean | "indeterminate";
  onToggleAll: () => void;
  onToggle: (licenseId: string) => void;
  showPerformActions: boolean;
  performingLicenseId: string | null;
  onPerform: (licenseId: string) => void;
  unassigningInspectionId: string | null;
  onClearAssignee: (inspectionId: string) => void;
  onOpenDetails: (licenseId: string) => void;
}) {
  const showActionsColumn =
    showPerformActions ||
    licenses.some(
      (item) => item.pending_inspection_id && (item.assignees?.length ?? 0) > 0,
    );

  return (
    <div className="rounded-md border">
      <div className="hidden lg:block w-full min-w-0">
        <table className="min-w-full divide-y divide-border">
          <thead
            className={
              pinHeader
                ? "[&_th]:sticky [&_th]:-top-px [&_th]:z-20 [&_th]:bg-muted"
                : "[&_th]:bg-muted"
            }
          >
            <tr>
              <th className="w-10 px-4 py-3.5 text-left">
                <Checkbox
                  aria-label="Select all properties"
                  checked={headerCheckboxState}
                  onCheckedChange={onToggleAll}
                />
              </th>
              <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                ID #
              </th>
              <th className="w-[28%] px-4 py-3.5 text-left text-sm font-semibold">
                Address
              </th>
              <th className="w-[18%] px-4 py-3.5 text-left text-sm font-semibold">
                Jurisdiction
              </th>
              <th className="w-[18%] px-4 py-3.5 text-left text-sm font-semibold">
                Assignee(s)
              </th>
              {showActionsColumn ? (
                <th className="sticky right-0 z-30 w-[14rem] min-w-[14rem] px-4 py-3.5 text-left text-sm font-semibold bg-muted shadow-[-1px_0_0_0_hsl(var(--border))]">
                  Actions
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {licenses.map((item) => {
              const selected = selectedIds.has(item.license_id);
              const isPerforming = performingLicenseId === item.license_id;
              const pendingInspectionId = item.pending_inspection_id;
              const canClearAssignee =
                pendingInspectionId != null &&
                (item.assignees?.length ?? 0) > 0;
              const isUnassigning =
                unassigningInspectionId === pendingInspectionId;
              return (
                <tr
                  key={item.license_id}
                  className="group cursor-pointer hover:bg-muted/50"
                  onClick={() => onOpenDetails(item.license_id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenDetails(item.license_id);
                    }
                  }}
                  tabIndex={0}
                >
                  <td
                    className="px-4 py-4"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      aria-label={`Select ${formatInspectionAddress(item)}`}
                      checked={selected}
                      onCheckedChange={() => onToggle(item.license_id)}
                    />
                  </td>
                  <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                    {item.display_id || "—"}
                  </td>
                  <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                    {formatInspectionAddress(item)}
                  </td>
                  <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                    {item.jurisdiction_name || "Jurisdiction"}
                  </td>
                  <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm text-muted-foreground">
                    {formatInspectionAssignees(item.assignees)}
                  </td>
                  {showActionsColumn ? (
                    <td
                      className="sticky right-0 z-20 w-[14rem] min-w-[14rem] px-4 py-4 text-sm bg-card group-hover:bg-muted/50 shadow-[-1px_0_0_0_hsl(var(--border))]"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <div className="flex min-w-max flex-wrap justify-end gap-2">
                        {showPerformActions ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={performingLicenseId != null}
                            onClick={() => onPerform(item.license_id)}
                          >
                            <ClipboardCheck className="h-4 w-4" />
                            {isPerforming ? "Opening…" : "Perform Inspection"}
                          </Button>
                        ) : null}
                        {canClearAssignee && pendingInspectionId ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={unassigningInspectionId != null}
                            onClick={() => onClearAssignee(pendingInspectionId)}
                          >
                            <UserRoundX className="h-4 w-4" />
                            {isUnassigning ? "Clearing…" : "Clear assignee"}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Checkbox
            id="select-all-dispatch-mobile"
            aria-label="Select all properties"
            checked={headerCheckboxState}
            onCheckedChange={onToggleAll}
          />
          <Label htmlFor="select-all-dispatch-mobile" className="font-normal">
            Select all ({selectedIds.size} selected)
          </Label>
        </div>
        <div className="divide-y divide-border">
          {licenses.map((item) => {
            const id = `dispatch-${item.license_id}`;
            const isPerforming = performingLicenseId === item.license_id;
            const pendingInspectionId = item.pending_inspection_id;
            const canClearAssignee =
              pendingInspectionId != null && (item.assignees?.length ?? 0) > 0;
            const isUnassigning =
              unassigningInspectionId === pendingInspectionId;
            return (
              <div key={item.license_id} className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={id}
                    className="mt-1"
                    checked={selectedIds.has(item.license_id)}
                    onCheckedChange={() => onToggle(item.license_id)}
                  />
                  <Label htmlFor={id} className="cursor-pointer font-normal">
                    <div className="font-medium">
                      {formatInspectionAddress(item)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item.jurisdiction_name || "Jurisdiction"}
                      {item.display_id ? ` · ${item.display_id}` : ""}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Assignee(s): {formatInspectionAssignees(item.assignees)}
                    </div>
                  </Label>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpenDetails(item.license_id)}
                  >
                    View details
                  </Button>
                  {showPerformActions ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={performingLicenseId != null}
                      onClick={() => onPerform(item.license_id)}
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      {isPerforming ? "Opening…" : "Perform Inspection"}
                    </Button>
                  ) : null}
                  {canClearAssignee && pendingInspectionId ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={unassigningInspectionId != null}
                      onClick={() => onClearAssignee(pendingInspectionId)}
                    >
                      <UserRoundX className="h-4 w-4" />
                      {isUnassigning ? "Clearing…" : "Clear assignee"}
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
